import { useState } from 'react'
import { Link, Form } from 'react-router'
import { DarkModeToggle } from '~/components/dark-mode-toggle'
import { AutoBreadcrumb } from '~/components/auto-breadcrumb'
import {
  Menu,
  X,
  LogOut,
  User,
  ChevronDown,
  Wrench,
  Zap,
  Paintbrush,
  HardHat,
  Hammer,
  Home,
  Scissors,
  Key,
  SprayCan,
  TreePine,
  Snowflake,
  Truck,
  Phone,
  MapPin,
  Map as MapIcon,
  Building2,
  BookOpen,
  FileText,
  Camera,
  Briefcase,
  Calculator,
  Star,
  BarChart3,
  Shield,
  Leaf,
} from 'lucide-react'

const SERVICE_LINKS = [
  { href: '/services/plomberie', label: 'Plomberie', icon: Wrench },
  { href: '/services/electricite', label: 'Électricité', icon: Zap },
  { href: '/services/peinture', label: 'Peinture', icon: Paintbrush },
  { href: '/services/renovation', label: 'Rénovation', icon: HardHat },
  { href: '/services/menuiserie', label: 'Menuiserie', icon: Hammer },
  { href: '/services/toiture', label: 'Toiture', icon: Home },
  { href: '/services/carrelage-sol', label: 'Carrelage / Sol', icon: Scissors },
  { href: '/services/serrurerie', label: 'Serrurerie', icon: Key },
  { href: '/services/nettoyage', label: 'Nettoyage', icon: SprayCan },
  { href: '/services/jardinage', label: 'Jardinage', icon: TreePine },
  { href: '/services/climatisation', label: 'Climatisation', icon: Snowflake },
  { href: '/services/demenagement', label: 'Déménagement', icon: Truck },
]

const CITY_LINKS = [
  { href: '/ville/paris', label: 'Paris' },
  { href: '/ville/lyon', label: 'Lyon' },
  { href: '/ville/marseille', label: 'Marseille' },
  { href: '/ville/toulouse', label: 'Toulouse' },
  { href: '/ville/bordeaux', label: 'Bordeaux' },
  { href: '/ville/nantes', label: 'Nantes' },
  { href: '/ville/lille', label: 'Lille' },
  { href: '/ville/nice', label: 'Nice' },
  { href: '/ville/strasbourg', label: 'Strasbourg' },
  { href: '/ville/montpellier', label: 'Montpellier' },
]

const REGION_LINKS = [
  { href: '/artisans/ile-de-france', label: 'Île-de-France' },
  { href: '/artisans/auvergne-rhone-alpes', label: 'Auvergne-Rhône-Alpes' },
  { href: '/artisans/provence-alpes-cote-d-azur', label: 'PACA' },
  { href: '/artisans/nouvelle-aquitaine', label: 'Nouvelle-Aquitaine' },
  { href: '/artisans/occitanie', label: 'Occitanie' },
  { href: '/artisans/hauts-de-france', label: 'Hauts-de-France' },
  { href: '/artisans/grand-est', label: 'Grand Est' },
  { href: '/artisans/bretagne', label: 'Bretagne' },
]

const MORE_LINKS = [
  { href: '/guide-prix', label: 'Guide des prix', icon: FileText },
  { href: '/cee', label: 'Primes CEE', icon: Leaf },
  { href: '/rge', label: 'Artisans RGE', icon: Shield },
  { href: '/barometre', label: 'Baromètre prix', icon: BarChart3 },
  { href: '/carte-artisans', label: 'Carte artisans', icon: MapIcon },
  { href: '/avant-apres', label: 'Avant / Après', icon: Camera },
  { href: '/metier', label: 'Fiches métiers', icon: Briefcase },
  { href: '/blog', label: 'Blog', icon: BookOpen },
  { href: '/simulateur-aides', label: 'Simulateur aides', icon: Calculator },
  { href: '/faq', label: 'FAQ', icon: Star },
]

interface NavbarProps {
  user?: { firstName: string; role: string } | null
}

export function Navbar({ user }: NavbarProps = {}) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [villesOpen, setVillesOpen] = useState(false)
  const [regionsOpen, setRegionsOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)

  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border glass">
        <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 lg:px-6">
          {/* Logo + trust signal */}
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                <Wrench className="h-4 w-4 text-white" aria-hidden="true" />
              </div>
              <span className="text-lg font-bold text-slate-900">
                Affiche<span className="text-primary">Services</span>
              </span>
            </Link>
            <span className="hidden items-center gap-1 border-l border-slate-200 pl-3 text-xs text-slate-500 lg:flex">
              <Shield className="h-3 w-3" aria-hidden="true" />
              Artisans SIRET vérifiés
            </span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden items-center gap-1 lg:flex">
            {/* Services dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                aria-expanded={servicesOpen}
                aria-haspopup="true"
                onClick={() => setServicesOpen(!servicesOpen)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setServicesOpen(false)
                }}
              >
                Services
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>

              {servicesOpen && (
                <div className="absolute left-0 top-full z-50 w-[560px] animate-fade-in rounded-xl border border-border bg-white p-5 shadow-xl">
                  <div className="grid grid-cols-2 gap-1">
                    {SERVICE_LINKS.slice(0, 8).map(({ href, label, icon: Icon }) => {
                      const prices: Record<string, string> = {
                        'Plomberie': 'dès 80 €', 'Électricité': 'dès 150 €', 'Peinture': 'dès 20 €/m²',
                        'Rénovation': 'dès 5 000 €', 'Menuiserie': 'dès 200 €', 'Toiture': 'dès 500 €',
                        'Serrurerie': 'dès 80 €', 'Nettoyage': 'dès 50 €',
                      }
                      return (
                      <Link
                        key={href}
                        to={href}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-600 transition-colors hover:bg-primary-50 hover:text-primary"
                      >
                        <Icon className="h-4 w-4" />
                        <span className="flex-1">{label}</span>
                        {prices[label] && <span className="text-[10px] text-slate-400">{prices[label]}</span>}
                      </Link>
                    )})}
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 border-t border-border pt-3">
                    <Link to="/services" className="rounded-lg bg-slate-50 px-3 py-2 text-center text-xs font-medium text-slate-700 hover:bg-primary-50 hover:text-primary">
                      Tous les services
                    </Link>
                    <Link to="/guide-prix" className="rounded-lg bg-slate-50 px-3 py-2 text-center text-xs font-medium text-slate-700 hover:bg-primary-50 hover:text-primary">
                      Guide des prix
                    </Link>
                    <Link to="/calculateur" className="rounded-lg bg-slate-50 px-3 py-2 text-center text-xs font-medium text-slate-700 hover:bg-primary-50 hover:text-primary">
                      Calculateur
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Villes dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setVillesOpen(true)}
              onMouseLeave={() => setVillesOpen(false)}
            >
              <button
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                onClick={() => setVillesOpen(!villesOpen)}
              >
                Villes
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${villesOpen ? 'rotate-180' : ''}`} />
              </button>
              {villesOpen && (
                <div className="absolute left-0 top-full z-50 w-64 animate-fade-in rounded-xl border border-border bg-white p-4 shadow-xl">
                  <div className="space-y-0.5">
                    {CITY_LINKS.map(({ href, label }) => (
                      <Link key={href} to={href} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-primary-50 hover:text-primary">
                        <MapPin className="h-3.5 w-3.5" /> {label}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-2 border-t border-border pt-2">
                    <Link to="/toutes-villes" className="block rounded-lg bg-slate-50 px-3 py-2 text-center text-xs font-medium text-slate-700 hover:bg-primary-50 hover:text-primary">
                      Toutes les villes
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Régions dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setRegionsOpen(true)}
              onMouseLeave={() => setRegionsOpen(false)}
            >
              <button
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                onClick={() => setRegionsOpen(!regionsOpen)}
              >
                Régions
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${regionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {regionsOpen && (
                <div className="absolute left-0 top-full z-50 w-64 animate-fade-in rounded-xl border border-border bg-white p-4 shadow-xl">
                  <div className="space-y-0.5">
                    {REGION_LINKS.map(({ href, label }) => (
                      <Link key={href} to={href} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-primary-50 hover:text-primary">
                        <Building2 className="h-3.5 w-3.5" /> {label}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 border-t border-border pt-2">
                    <Link to="/regions" className="rounded-lg bg-slate-50 px-3 py-2 text-center text-xs font-medium text-slate-700 hover:bg-primary-50 hover:text-primary">
                      Toutes les régions
                    </Link>
                    <Link to="/departements" className="rounded-lg bg-slate-50 px-3 py-2 text-center text-xs font-medium text-slate-700 hover:bg-primary-50 hover:text-primary">
                      Départements
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* Plus dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setMoreOpen(true)}
              onMouseLeave={() => setMoreOpen(false)}
            >
              <button
                className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                onClick={() => setMoreOpen(!moreOpen)}
              >
                Plus
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full z-50 w-72 animate-fade-in rounded-xl border border-border bg-white p-4 shadow-xl">
                  <div className="grid grid-cols-2 gap-1">
                    {MORE_LINKS.map(({ href, label, icon: Icon }) => (
                      <Link key={href} to={href} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-primary-50 hover:text-primary">
                        <Icon className="h-3.5 w-3.5" /> {label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Urgences CTA */}
            <Link
              to="/urgence"
              className="flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100"
            >
              <Phone className="h-3.5 w-3.5" /> Urgences 24h
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden items-center gap-3 lg:flex">
            {user ? (
              <>
                <Link
                  to={user.role === 'professional' ? '/espace-pro' : '/tableau-de-bord'}
                  className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 btn-press"
                >
                  <User className="h-4 w-4" />
                  {user.firstName}
                </Link>
                <Form method="post" action="/logout">
                  <button type="submit" className="rounded-lg p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-600 btn-press" title="Deconnexion">
                    <LogOut className="h-4 w-4" />
                  </button>
                </Form>
                <DarkModeToggle />
                <Link to="/demande-devis" className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-primary-hover hover:shadow-lg hover:scale-[1.02] btn-press">
                  Devis gratuit
                </Link>
              </>
            ) : (
              <>
                <Link to="/devenir-pro" className="text-sm font-medium text-slate-600 hover:text-slate-900">
                  Espace Pro
                </Link>
                <DarkModeToggle />
                <Link to="/login" className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 btn-press">
                  Connexion
                </Link>
                <Link to="/demande-devis" className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-primary-hover hover:shadow-lg hover:scale-[1.02] btn-press">
                  Devis gratuit
                </Link>
              </>
            )}
          </div>

          {/* Mobile right cluster: quick-devis CTA + burger */}
          <div className="flex items-center gap-1 lg:hidden">
            <Link
              to="/demande-devis"
              aria-label="Demander un devis gratuit"
              className="flex h-11 items-center justify-center rounded-lg bg-primary px-3 text-xs font-semibold text-white hover:bg-primary-hover active:scale-95"
            >
              Devis
            </Link>
            <button
              onClick={() => setMobileOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-lg text-slate-600 hover:bg-slate-50 active:scale-95"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
        {/* Breadcrumb attached to header — single visual unit instead of two stacked bars */}
        <AutoBreadcrumb />
      </header>

      {/* Mobile slide-out */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="fixed inset-y-0 right-0 z-50 w-80 animate-slide-in bg-white shadow-2xl">
            <div className="flex h-16 items-center justify-between border-b border-border px-4">
              <span className="font-bold text-slate-900">Menu</span>
              <button
                onClick={() => setMobileOpen(false)}
                className="rounded-lg p-2 text-slate-400 hover:bg-slate-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto p-4">
              {/* CTA */}
              <Link
                to="/demande-devis"
                onClick={() => setMobileOpen(false)}
                className="mb-4 block w-full rounded-lg bg-primary px-4 py-3 text-center text-sm font-semibold text-white shadow-sm"
              >
                Demander un devis gratuit
              </Link>

              {/* Services */}
              <div className="mb-4">
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Services
                </p>
                <div className="space-y-0.5">
                  {SERVICE_LINKS.slice(0, 8).map(({ href, label, icon: Icon }) => (
                    <Link
                      key={href}
                      to={href}
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50"
                    >
                      <Icon className="h-4 w-4 text-slate-400" />
                      {label}
                    </Link>
                  ))}
                  <Link
                    to="/services"
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2 text-sm font-medium text-primary"
                  >
                    Tous les services →
                  </Link>
                </div>
              </div>

              {/* Villes */}
              <div className="border-t border-border pt-4">
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Villes</p>
                <div className="grid grid-cols-2 gap-0.5">
                  {CITY_LINKS.slice(0, 6).map(({ href, label }) => (
                    <Link key={href} to={href} onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                      {label}
                    </Link>
                  ))}
                </div>
                <Link to="/toutes-villes" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm font-medium text-primary">
                  Toutes les villes →
                </Link>
              </div>

              {/* Régions */}
              <div className="border-t border-border pt-4">
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Régions</p>
                <div className="grid grid-cols-2 gap-0.5">
                  {REGION_LINKS.slice(0, 6).map(({ href, label }) => (
                    <Link key={href} to={href} onClick={() => setMobileOpen(false)} className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                      {label}
                    </Link>
                  ))}
                </div>
                <div className="flex gap-2 px-3 pt-1">
                  <Link to="/regions" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-primary">Régions →</Link>
                  <Link to="/departements" onClick={() => setMobileOpen(false)} className="text-sm font-medium text-primary">Départements →</Link>
                </div>
              </div>

              {/* Plus */}
              <div className="border-t border-border pt-4">
                <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Ressources</p>
                {MORE_LINKS.slice(0, 6).map(({ href, label, icon: Icon }) => (
                  <Link key={href} to={href} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
                    <Icon className="h-4 w-4 text-slate-400" /> {label}
                  </Link>
                ))}
              </div>

              {/* Urgence + Pro */}
              <div className="border-t border-border pt-4">
                <Link to="/urgence" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-sm font-semibold text-red-700">
                  <Phone className="h-4 w-4" /> Urgences 24h
                </Link>
                <Link to="/devenir-pro" onClick={() => setMobileOpen(false)} className="mt-2 block rounded-lg px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
                  Espace Pro
                </Link>
              </div>

              {/* Auth */}
              <div className="mt-4 border-t border-border pt-4">
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="block w-full rounded-lg border border-border px-4 py-2.5 text-center text-sm font-medium text-slate-700"
                >
                  Se connecter
                </Link>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
